// Ported from dev-scrips-pm-react src/billing/lib/payment-errors.ts (PROD-1119 cross-module closure).
/**
 * Payment-mutation error helpers (PROD-798).
 *
 * Centralises the cashier-shift escalation logic shared by
 * ReceivePaymentDialog / VoidPaymentDialog / RefundPaymentDialog. The
 * pattern: payment dialogs catch the mutation rejection, ask this
 * helper whether the host wants to handle the cashier-shift error
 * inline (vs. the global toast), and dispatch accordingly.
 *
 * Why a helper:
 *   1. Three dialogs were copy-pasting the same string match + early
 *      return in their catch blocks (Samer review on PR #129).
 *   2. The next change (V2 — auto-suppress the global toast on this
 *      error class) needs to land in one place, not three.
 *
 * Error envelope (see `src/shared/api/api-client.ts:16-26`):
 *   `ApiError` has `.body` (raw response text) AND `.message`
 *   (`"API Error <status>: <body>"`). Match on `.body` first (stable
 *   when the wrapper changes), `.message` as fallback for non-ApiError
 *   throws.
 *
 * Wire-shape note: the backend currently returns the verbatim Flutter
 * string `"Cashier's shift is not open"` as the 400 response body when
 * a payment action fires without an open shift. There is NO structured
 * error code on this branch — Flutter `billing_tab.dart:136` matches
 * the literal string verbatim (`state.message == "Cashier's shift is
 * not open"`); we mirror that contract. If the backend ever introduces
 * a stable code (e.g. `code: "CASHIER_SHIFT_NOT_OPEN"`), update the
 * matcher here once and every payment dialog inherits the change.
 */

import { ApiError } from '@/shared/api/api-client';

/**
 * Verbatim string the .NET billing pipeline returns for the
 * cashier-shift-not-open error. Copy-pasted from Flutter
 * `billing_tab.dart:136` — DO NOT paraphrase. Any change must be
 * verified against a live 400 response body first.
 */
export const CASHIER_SHIFT_NOT_OPEN_MSG = "Cashier's shift is not open";

/**
 * Returns true when the error reads as "Cashier's shift is not open".
 * Checks `ApiError.body` first (raw server text, stable across
 * Error-wrapper changes), then `.message` as a fallback so non-ApiError
 * throws (network wrappers, future Error subclasses) still match.
 */
export function isCashierShiftNotOpen(err: unknown): boolean {
  if (err instanceof ApiError) {
    if (err.body && err.body.includes(CASHIER_SHIFT_NOT_OPEN_MSG)) return true;
  }
  if (err instanceof Error && err.message.includes(CASHIER_SHIFT_NOT_OPEN_MSG)) {
    return true;
  }
  return false;
}

/**
 * Standard payment-dialog catch-block handler. Returns `'handled'`
 * when the error is the cashier-shift case AND the host supplied an
 * escalation callback (the dialog should close + the host opens
 * `OpenCashierShiftDialog`). Returns `'unhandled'` for every other
 * case — the dialog stays open and the global error toast surfaces
 * the failure as before.
 *
 * Usage:
 *   } catch (err) {
 *     if (handlePaymentError(err, onCashierShiftRequired) === 'handled') {
 *       onOpenChange(false);
 *       return;
 *     }
 *     // unhandled → leave dialog open, global toast already shown
 *   }
 */
export function handlePaymentError(
  err: unknown,
  onCashierShiftRequired?: () => void,
): 'handled' | 'unhandled' {
  if (onCashierShiftRequired && isCashierShiftNotOpen(err)) {
    onCashierShiftRequired();
    return 'handled';
  }
  return 'unhandled';
}
